import { booleanAttribute, computed, Directive, inject, input } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Turns an `<a>` into an SSR-safe internal/external link.
 *
 * The `href` is set declaratively through host bindings, so it is correct in the
 * server-rendered markup and hydration-friendly (unlike a post-render DOM rewrite):
 * - External `http(s)` URLs get `target="_blank" rel="noopener noreferrer"` and are left to the browser,
 *   unless `openInSameTab` pins them to the current tab.
 * - Root-relative in-app routes are navigated through the Router on click; everything else
 *   (`mailto:`, `tel:`, protocol-relative `//host`, …) is left to the browser.
 *
 * Because it enhances the consumer's own `<a>`, every other attribute (`class`, `aria-label`,
 * `title`, `download`, …) sits on the anchor exactly as written.
 *
 * @example
 * ```html
 * <a [appLink]="item.url">{{ item.label }}</a>
 * <a [appLink]="card.url" class="block h-60 rounded-3xl"><app-image ... /></a>
 * ```
 */
@Directive({
  selector: 'a[appLink]',
  standalone: true,
  host: {
    '[attr.href]': 'appLink()',
    '[attr.target]': 'opensInNewTab() ? "_blank" : null',
    '[attr.rel]': 'opensInNewTab() ? "noopener noreferrer" : null',
    '(click)': 'onClick($event)',
  },
})
export class LinkDirective {
  /** Destination URL. Root-relative paths are routed internally; everything else (http, mailto, tel…) is left to the browser. */
  readonly appLink = input.required<string>();

  /** CMS opt-in, carried by menu links only: keeps an external URL in the current tab. */
  readonly openInSameTab = input(false, { transform: booleanAttribute });

  /** `http(s)` targets open in a new tab, unless the CMS pinned this link to the current one. */
  protected readonly opensInNewTab = computed(() => (this.appLink() ?? '').startsWith('http') && !this.openInSameTab());

  private readonly router = inject(Router);

  /** Only root-relative paths (but not protocol-relative `//host`) are in-app routes. */
  private readonly isInternalRoute = computed(() => {
    const url = this.appLink() ?? '';
    return url.startsWith('/') && !url.startsWith('//');
  });

  protected onClick(event: MouseEvent): void {
    // Let the browser handle anything that is not an in-app route (external, mailto:, tel:, …).
    if (!this.isInternalRoute()) return;
    // Preserve the browser's open-in-new-tab / download affordances, like RouterLink does.
    if (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return;
    event.preventDefault();
    void this.router.navigateByUrl(this.appLink());
  }
}
